import { useState, useEffect, useRef, useMemo } from "react";

import api from "@helpers/api.js";
import Parser from "@helpers/parser";
import { useWallets, walletIsConnected } from "@helpers/wallets";

import "./styles.css";

import CustomModal from "@components/CustomModal/index.js";

import * as Material from "@mui/material";

import SendIcon from "@mui/icons-material/Send";

import CheckRoundedIcon from "@mui/icons-material/CheckRounded";

import {
    AccountBalanceRounded,
    AttachMoneyRounded,
    AutoModeRounded,
    CreditCardRounded,
    ManageHistoryRounded,
    PixRounded,
    RadioButtonUnchecked,
    SellRounded,
} from "@mui/icons-material";


import { Input, Button, Text } from '@fragments'

const intervalsConfig = [
    {
        interval: "week",
        label: "Semanal",
    },
    {
        interval: "month",
        label: "Mensal",
    },
    {
        interval: "bimonthly",
        label: "Bimestral",
    },
    {
        interval: "quarterly",
        label: "Trimestral",
    },
    {
        interval: "semiannual",
        label: "Semestral",
    },
    {
        interval: "year",
        label: "Anual",
    },
    {
        "interval": "lifetime",
        "label": "Vitalício"
    }
];

function AnticipationConfig({ info, setInfo, anticipationAvailable = false, loading = false }) {
    const [selectedFlow, setSelectedFlow] = useState(info?.anticipation?.enabled ? 'anticipated' : 'normal');

    const handleFlowSelect = (flowValue) => {
        setSelectedFlow(flowValue);
        setInfo({
            ...info,
            anticipation: {
                ...info.anticipation,
                enabled: flowValue === 'anticipated'
            }
        })
    };

    function Flow({ value, title, description }) {

        const isSelected = value === selectedFlow

        if (loading) return null;

        const disabled = !anticipationAvailable && value === 'anticipated'

        function handleClick() {
            if (disabled) {
                window.setSnackbar('Complete o cadastro da sua carteira para habilitar')
                return;
            }
            handleFlowSelect(value)
        }

        return (
            <div
                style={{
                    cursor: disabled ? 'not-allowed' : 'pointer',
                }}
                className={`flow-option ${isSelected ? 'selected' : ''}`}
                onClick={handleClick}
            >
                <div className="flow-header px-1 py-1">
                    <Material.Radio
                        disabled={disabled}
                        checked={isSelected}
                        onChange={handleClick}
                        color="primary"
                    />
                    <h5 className="bold">{title} </h5>
                    {disabled
                        ? <Button
                            color="primary"
                            className="ml-2"
                            size="small"
                            style={{
                                alignSelf: 'flex-end',
                                transform: "translateY(-4px)"
                            }}
                            onClick={() => window.navigate('banking')}
                            text="Complete cadastro para habilitar"
                        /> : null}
                </div>



                <Material.Divider flexItem />

                <div className="flow-details px-2 py-2">
                    {description}
                </div>
            </div>
        );
    }

    return (
        <>
            {
                loading ? (
                    <>
                        <Material.Skeleton variant="rectangular" width="100%" height={100} className="mt-2" />
                        <Material.Skeleton variant="rectangular" width="100%" height={100} className="mt-1" />

                    </>
                ) : (
                    <div className="anticipation-config">
                        <div className="flow-options">
                            <Flow
                                value="anticipated"
                                title="Fluxo antecipado"
                                description="Quando a cobrança é confirmada, após 7 dias é realizada uma solicitação de antecipação, que é analisada dentro de 2 dias úteis para ser aprovada e disponibilizada ao saldo. (Recebimento em D+10 em média)"
                            />

                            <Flow
                                value="normal"
                                title="Fluxo prolongado"
                                description="Cada parcela será disponibilizada ao saldo em D+32 mês a mês"
                            />
                        </div >
                    </div >
                )
            }
        </>
    )
}

export default function ConfigPlanModal({ modalRef, config }) {
    const [open, setOpen] = useState(false);

    const [loading, setLoading] = useState(false)

    const REF_config = useRef();

    const [saving, setSaving] = useState(false)
    const [info, setInfo] = useState(null)

    const REF_callback = useRef();
    const { asaas, stripe, loading: loadingWallets } = useWallets();
    const stripeConnected = walletIsConnected(stripe);
    const asaasConnected = walletIsConnected(asaas);

    modalRef.current = {
        open({ id, community, mode }, callback) {
            REF_config.current = { id, community, mode };
            REF_callback.current = callback;
            setInfo(null)
            handleLoadPlan(id)
            setOpen(true);
        },
        close() {
            setOpen(false);
        },
    };

    const defaultConfig = {
        label: "Premium",
        interval: "year",
        installments: 12,
        price: 29700,
        usd_price: null,
        anticipation: {
            enabled: false
        },
        autorenew: true,
        enable_installments: true,
        methods: {
            pix: {
                enabled: true,
            },
            credit: {
                enabled: true,
            },
        },
    }

    const installmentsAvailable = getMaxInstallmentsAvailable();


    useEffect(() => {
        if (info?.interval === "lifetime") {
            handleUpdate('autorenew', false)
        }
    }, [info?.interval])

    async function handleLoadPlan(id = null) {
        if (!id) {
            setInfo({ ...defaultConfig })
            return
        }

        setLoading(true)

        try {
            const { data } = await api.get(`/community/plan/${id}`)
            setInfo({ ...data, enable_installments: !!data.installments })
        } catch (e) {
            console.log(e)
        }

        setLoading(false)
    }

    function getMaxInstallmentsAvailable(value = info?.price) {
        if (!value) return 1;

        const minPricePerInstallment = 600; // R$ 6,00 em centavos
        const maxInstallments = 12;

        // Calcula quantas parcelas são possíveis com o preço mínimo
        const possibleInstallments = Math.floor(value / minPricePerInstallment);

        // Retorna o menor valor entre as parcelas possíveis e o máximo permitido
        const result = Math.min(Math.max(possibleInstallments, 1), maxInstallments);

        return result
    }

    const InputHandler = {
        method(method) {
            console.log(method)
            handleUpdate('methods', {
                pix: {
                    enabled: method.includes('pix')
                },
                credit: {
                    enabled: method.includes('credit')
                }
            })
        },
        enableInstallments(value) {
            // Se está habilitando parcelamento (de false para true)
            if (value && !info?.enable_installments) {
                // Define installments com o máximo disponível
                const maxInstallments = getMaxInstallmentsAvailable(info?.price);
                setInfo({
                    ...info,
                    enable_installments: value,
                    installments: maxInstallments
                });
            } else {
                // Se está desabilitando, apenas atualiza enable_installments
                handleUpdate('enable_installments', value);
            }
        },
        interval(value) {
            handleUpdate('interval', value)
        },
        label(value) {
            handleUpdate('label', value)
        },
        price(value) {
            handleUpdate('price', value?.replace(/\D/g, ""))
        },
        usdPrice(value) {
            handleUpdate('usd_price', value?.replace(/\D/g, ""))
        },
        installments(value) {
            handleUpdate('installments', parseInt(value))
        },
        autorenew(value) {
            handleUpdate('autorenew', value)
        }
    }

    function handleUpdate(key, value) {
        const priceCopy = { ...info };
        priceCopy[key] = value;
        if (key === 'price') {
            priceCopy['installments'] = getMaxInstallmentsAvailable(value)
        }
        setInfo({ ...priceCopy });
    }

    async function handleSubmit() {
        try {
            const payload = {
                label: info.label,
                anticipation: info.anticipation,
                installments: info.enable_installments ? (info.installments || installmentsAvailable) : (null),
                methods: Object.keys(info.methods).filter(p => {
                    return info.methods[p].enabled
                })
            }

            if (info.id) {
                payload.id = info.id
            } else {
                payload.community = REF_config.current.community
                payload.price = info.price
                payload.interval = info.interval
                payload.autorenew = info.autorenew
            }

            if (stripeConnected && info.usd_price !== null && info.usd_price !== undefined && info.usd_price !== '') {
                payload.usd_price = info.usd_price
            }

            setSaving(true)

            const method = info?.id ? 'put' : 'post'
            const endpoint = {
                post: `/community/${REF_config.current.community}/plan`,
                put: `/community/plan/${info.id}`
            }[method]

            const { data } = await api[method](endpoint, payload)

            if (data?.id) {
                await REF_callback.current(data);
                window.setSnackbar('Plano Salvo com sucesso')
                setOpen(false)
            }
        } catch (e) {
            console.log(e)
        }

        setSaving(false)
    }



    const hasInstallmentsEnabled = !!info?.enable_installments

    const activeMethods = Object.keys(info?.methods || {})?.filter(p => {
        return info.methods[p].enabled
    })

    return (
        <CustomModal
            maxWidth={490}
            bottomButtonSumbitText="Concluir edição"
            bottomButtonSumbitIcon={<SendIcon />}
            loading={loading || saving}
            title="Configurar plano"
            handleSubmit={handleSubmit}
            open={open}
            handleClose={() => setOpen(false)}
            bodyComponent={
                <div className="px-4 py-4" >

                    {
                        info && <div
                            className="plan-config-container pt-3"
                            style={{ alignItems: "flex-start" }}
                        >
                            <Input.Text
                                label="Nome do plano"
                                value={info.label}
                                onInput={InputHandler.label}
                                className="mr-1"
                            />


                            <Text.h3
                                value="Preço do plano"
                                className="mt-4 mb-1"
                                left={<SellRounded />}
                            />
                            <Text.span
                                value="Preencha estes campos com cautela, pois uma vez criados, não poderão ser editados"
                                contrast={6}
                            />

                            <Input.Money
                                disabled={info?.id}
                                label="Preço do plano"
                                placeholder="Preço"
                                className="mt-2"
                                value={Parser.price.brl(info?.price)}
                                onInput={InputHandler.price}
                            />

                            {stripeConnected && (
                                <>
                                    <Input.Money
                                        start="US$"
                                        label="Preco internacional (USD)"
                                        placeholder="0.00"
                                        className="mt-2"
                                        value={Parser.price.usd(info?.usd_price)}
                                        onInput={InputHandler.usdPrice}
                                    />
                                    <Text.caption
                                        contrast={6}
                                        className="mt-1"
                                    >
                                        Aplica-se a ambas as carteiras; o valor em USD sera usado na carteira internacional.
                                    </Text.caption>
                                </>
                            )}

                            {
                                !['week', 'month'].includes(info.interval) && (
                                    <div className="mt-1" key={Math.random()}>
                                        <Input.Switch
                                            label="Permitir parcelamento"
                                            value={hasInstallmentsEnabled}
                                            onInput={InputHandler.enableInstallments}
                                        />

                                        {
                                            hasInstallmentsEnabled && (
                                                <Input.Select
                                                    key={Math.random()}
                                                    className="mt-1"
                                                    label="Quantidade máxima de parcelas"
                                                    disabled={!hasInstallmentsEnabled}
                                                    value={hasInstallmentsEnabled ? (info.installments) : '-'}
                                                    onInput={InputHandler.installments}
                                                    options={Array.from({ length: installmentsAvailable }, (_, index) => ({
                                                        label: `Em até ${index + 1}x no crédito`,
                                                        value: index + 1,
                                                    }))}
                                                />
                                            )
                                        }
                                    </div>
                                )
                            }



                            <Text.h3
                                value="Mecânica de renovação"
                                className="mt-4 mb-1"
                                left={<AutoModeRounded />}
                            />
                            <Text.span
                                value="Indique se a renovação estará ativa ou não, e o período de acesso que os assinantes do plano terão direito aos recursos."
                                contrast={6}
                            />


                            <Input.Switch
                                key={`${info?.interval}-${info?.autorenew}`}
                                disabled={info?.id || info?.interval === "lifetime"}
                                label="Renovação automática"
                                value={info?.autorenew}
                                onInput={InputHandler.autorenew}
                            />

                            <Input.Select
                                label="Período de acesso"
                                disabled={info?.id}
                                className="mt-1"
                                value={info.interval}
                                onInput={InputHandler.interval}
                                options={intervalsConfig.map((v) => ({
                                    label: v.label,
                                    value: v.interval,
                                }))}
                            />

                            <Text.h3
                                value="Métodos de pagamento"
                                className="mt-4"
                                left={<AttachMoneyRounded />}
                            />
                            <Input.Choices
                                value={activeMethods}
                                className="mt-1"
                                onInput={InputHandler.method}
                                options={[
                                    {
                                        label: "Crédito",
                                        value: "credit",
                                        left: <CreditCardRounded fontSize="small" />
                                    },
                                    {
                                        label: "PIX",
                                        value: "pix",
                                        left: <PixRounded fontSize="small" />
                                    }
                                ]}
                                multiple
                            />


                            {asaasConnected && (
                                <>
                                    <Text.h3
                                        left={<ManageHistoryRounded />}
                                        value="Mecânica de recebíveis"
                                        className="mt-5 mb-1"
                                    />
                                    <Text.span
                                        value="Defina se as cobranças via crédito deverão ser adicionadas ao saldo de forma antecipada, ou a prazo"
                                        contrast={6}
                                    />


                                    <AnticipationConfig
                                        info={info}
                                        setInfo={setInfo}
                                        anticipationAvailable={asaasConnected}
                                        loading={loadingWallets}
                                    />
                                </>
                            )}
                        </div>
                    }
                </ div>
            }
            modalClass="cp-modal"
        />
    );
}
